import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Search } from "lucide-react";
import { toast } from "sonner";

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex items-start justify-between mb-6 ync-fade-up">
    <div>
      <h1 className="font-display text-3xl lg:text-4xl text-white font-light tracking-tight">{title}</h1>
      {subtitle && <p className="text-slate-400 mt-1 text-sm max-w-2xl">{subtitle}</p>}
    </div>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

export const Card = ({ children, className = "", ...rest }) => (
  <div className={`bg-[#1E293B] border border-white/5 rounded-lg ${className}`} {...rest}>{children}</div>
);

const STATUS_STYLES = {
  Open: "bg-[#DC2626]/15 text-[#f87171]", "In Progress": "bg-[#C9A24B]/15 text-[#C9A24B]",
  "Pending Review": "bg-[#64748B]/20 text-slate-300", Resolved: "bg-[#059669]/15 text-[#34d399]",
  Closed: "bg-[#059669]/15 text-[#34d399]", Active: "bg-[#059669]/15 text-[#34d399]",
  Inactive: "bg-[#64748B]/20 text-slate-400",
  Current: "bg-[#059669]/15 text-[#34d399]", "Overdue 0-15": "bg-[#C9A24B]/15 text-[#C9A24B]",
  "Overdue 16-30": "bg-[#DC2626]/15 text-[#f87171]", "Overdue 30+": "bg-[#DC2626]/25 text-[#f87171]",
  Cleared: "bg-[#059669]/15 text-[#34d399]", "Written Off": "bg-[#64748B]/20 text-slate-400",
  Positive: "bg-[#059669]/15 text-[#34d399]", Neutral: "bg-[#64748B]/20 text-slate-300",
  Negative: "bg-[#DC2626]/15 text-[#f87171]",
  Low: "bg-[#64748B]/20 text-slate-300", Medium: "bg-[#C9A24B]/15 text-[#C9A24B]",
  High: "bg-[#DC2626]/15 text-[#f87171]", Critical: "bg-[#DC2626]/30 text-[#fca5a5]",
  Upsell: "bg-[#C9A24B]/15 text-[#C9A24B]", "Cross-sell": "bg-[#38bdf8]/15 text-[#38bdf8]",
  Offered: "bg-[#64748B]/20 text-slate-300", Accepted: "bg-[#059669]/15 text-[#34d399]",
  Declined: "bg-[#DC2626]/15 text-[#f87171]", "Under Treatment": "bg-[#C9A24B]/15 text-[#C9A24B]",
  Manager: "bg-[#C9A24B]/15 text-[#C9A24B]", "Relationship Executive": "bg-[#38bdf8]/15 text-[#38bdf8]",
  Admin: "bg-[#059669]/15 text-[#34d399]",
  "Follow-up": "bg-[#64748B]/20 text-slate-300", Due: "bg-[#C9A24B]/15 text-[#C9A24B]",
  Review: "bg-[#38bdf8]/15 text-[#38bdf8]", Connected: "bg-[#059669]/15 text-[#34d399]",
  "Not picked": "bg-[#DC2626]/15 text-[#f87171]", Callback: "bg-[#C9A24B]/15 text-[#C9A24B]",
  Booked: "bg-[#059669]/15 text-[#34d399]", Refused: "bg-[#DC2626]/15 text-[#f87171]",
  Pending: "bg-[#64748B]/20 text-slate-300", Visited: "bg-[#C9A24B]/15 text-[#C9A24B]",
  Converted: "bg-[#059669]/15 text-[#34d399]", Written: "bg-[#64748B]/20 text-slate-300",
  Video: "bg-[#38bdf8]/15 text-[#38bdf8]",
};

export const StatusBadge = ({ value }) => (
  <Badge className={`border-0 font-medium ${STATUS_STYLES[value] || "bg-[#64748B]/20 text-slate-300"}`}>{value}</Badge>
);

export const Rating = ({ value }) => {
  if (!value) return <span className="text-slate-600">—</span>;
  const color = value >= 4 ? "text-[#34d399]" : value >= 3 ? "text-[#C9A24B]" : "text-[#f87171]";
  return <span className={`font-mono-ync font-semibold ${color}`}>{value}</span>;
};

function PatientSelect({ value, onPick }) {
  const [patients, setPatients] = useState([]);
  useEffect(() => { api.get("/patients").then((r) => setPatients(r.data)).catch(() => {}); }, []);
  return (
    <Select value={value || ""} onValueChange={(id) => {
      const p = patients.find((x) => x.id === id);
      onPick({ patient_id: id, patient_name: p?.patient_name || "", branch: p?.branch });
    }}>
      <SelectTrigger data-testid="field-patient_id" className="bg-[#0F172A] border-white/10 text-white"><SelectValue placeholder="Select patient" /></SelectTrigger>
      <SelectContent className="bg-[#1E293B] border-white/10 text-white max-h-72">
        {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.patient_name} · {p.branch}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function FieldInput({ field, value, onChange, onChangeMulti, meta }) {
  const common = "bg-[#0F172A] border-white/10 text-white";
  if (field.type === "patient")
    return <PatientSelect value={value} onPick={onChangeMulti} />;
  if (field.type === "branch")
    return (<>
      <Input list="ync-branch-list" data-testid={`field-${field.key}`} className={common} value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} placeholder="Select or type new branch" />
      <datalist id="ync-branch-list">{(meta.branches || []).map((b) => <option key={b} value={b} />)}</datalist>
    </>);
  if (field.type === "textarea")
    return <Textarea data-testid={`field-${field.key}`} className={common} value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} />;
  if (field.type === "switch")
    return <div className="pt-1"><Switch data-testid={`field-${field.key}`} checked={!!value} onCheckedChange={(v) => onChange(field.key, v)} /></div>;
  if (field.type === "select") {
    const opts = field.optionsKey ? (meta[field.optionsKey] || []) : field.options;
    return (
      <Select value={value || ""} onValueChange={(v) => onChange(field.key, v)}>
        <SelectTrigger data-testid={`field-${field.key}`} className={common}><SelectValue placeholder="Select" /></SelectTrigger>
        <SelectContent className="bg-[#1E293B] border-white/10 text-white">
          {opts?.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "date")
    return <Input data-testid={`field-${field.key}`} type="date" className={common} value={(value || "").slice(0, 10)} onChange={(e) => onChange(field.key, e.target.value)} />;
  if (field.type === "number")
    return <Input data-testid={`field-${field.key}`} type="number" min={field.min} max={field.max} step={field.step} className={common} value={value ?? ""} onChange={(e) => onChange(field.key, e.target.value === "" ? null : Number(e.target.value))} />;
  return <Input data-testid={`field-${field.key}`} className={common} value={value || ""} onChange={(e) => onChange(field.key, e.target.value)} />;
}

export function CrudTable({ title, subtitle, endpoint, columns, fields, defaults = {}, extraTop, onData, testid, dateField, filledBy = true }) {
  const { branch, meta, loadNotifications, person, dateRange, team } = useApp();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (branch !== "All") params.branch = branch;
      if (filledBy && person) params.person = person;
      if (dateField && dateRange?.from) { params.date_from = dateRange.from; params.date_to = dateRange.to; params.date_field = dateField; }
      const res = await api.get(`/${endpoint}`, { params });
      setRows(res.data);
      onData && onData(res.data);
    } catch { toast.error("Failed to load data"); }
    finally { setLoading(false); }
  }, [endpoint, branch, dateField, dateRange, person, filledBy]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm({ ...defaults, branch: branch !== "All" ? branch : (meta.branches?.[0] || ""), ...(filledBy ? { entered_by: person || "" } : {}) }); setDialogOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setDialogOpen(true); };
  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onChangeMulti = (obj) => setForm((f) => ({ ...f, ...obj }));

  const save = async () => {
    const fb = form.entered_by || person || "";
    const body = filledBy ? { ...form, entered_by: fb, person: fb } : form;
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, body);
      else await api.post(`/${endpoint}`, body);
      toast.success(editing ? "Updated" : "Created");
      setDialogOpen(false);
      load();
      loadNotifications && loadNotifications();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/${endpoint}/${deleteId}`);
      toast.success("Deleted");
      setDeleteId(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  const filtered = rows.filter((r) =>
    !search || JSON.stringify(r).toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid={`${testid}-search`} value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…" className="pl-9 w-56 bg-[#1E293B] border-white/10 text-white" />
        </div>
        <Button data-testid={`${testid}-add`} onClick={openCreate} className="bg-[#C9A24B] text-[#0F172A] hover:bg-[#b8923f] font-medium">
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </PageHeader>

      {extraTop}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                {columns.map((c) => (
                  <TableHead key={c.key} className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium whitespace-nowrap">{c.label}</TableHead>
                ))}
                {filledBy && <TableHead className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium whitespace-nowrap">Filled By</TableHead>}
                <TableHead className="text-right text-[10px] uppercase tracking-[0.15em] text-slate-500">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={columns.length + (filledBy ? 2 : 1)} className="text-center text-slate-500 py-10">Loading…</TableCell></TableRow>}
              {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={columns.length + (filledBy ? 2 : 1)} className="text-center text-slate-500 py-10">No records. Click Add to create one.</TableCell></TableRow>}
              {!loading && filtered.map((row) => (
                <TableRow key={row.id} data-testid={`${testid}-row`} className="border-white/5 hover:bg-[#0F172A]/40">
                  {columns.map((c) => (
                    <TableCell key={c.key} className="text-sm text-slate-200 whitespace-nowrap">
                      {c.render ? c.render(row) : (row[c.key] ?? <span className="text-slate-600">—</span>)}
                    </TableCell>
                  ))}
                  {filledBy && <TableCell className="text-sm whitespace-nowrap"><span className={row.entered_by || row.person ? "text-slate-300" : "text-slate-600"}>{row.entered_by || row.person || "Unassigned"}</span></TableCell>}
                  <TableCell className="text-right whitespace-nowrap">
                    <Button data-testid={`${testid}-edit`} size="icon" variant="ghost" onClick={() => openEdit(row)} className="h-8 w-8 text-slate-400 hover:text-white hover:bg-[#0F172A]">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button data-testid={`${testid}-delete`} size="icon" variant="ghost" onClick={() => setDeleteId(row.id)} className="h-8 w-8 text-slate-400 hover:text-[#f87171] hover:bg-[#DC2626]/10">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display font-light text-2xl">{editing ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            {filledBy && (
              <div className="col-span-2">
                <Label className="text-slate-300 text-sm mb-1.5 block">Filled By</Label>
                <Select value={form.entered_by || ""} onValueChange={(v) => onChange("entered_by", v)}>
                  <SelectTrigger data-testid="field-filled-by" className="bg-[#0F172A] border-white/10 text-white"><SelectValue placeholder="Select staff" /></SelectTrigger>
                  <SelectContent className="bg-[#1E293B] border-white/10 text-white max-h-72">
                    {(team || []).map((t) => <SelectItem key={t.id} value={t.name}>{t.name}{t.role ? ` · ${t.role}` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "col-span-2" : ""}>
                <Label className="text-slate-300 text-sm mb-1.5 block">{f.label}</Label>
                <FieldInput field={f} value={form[f.key]} onChange={onChange} onChangeMulti={onChangeMulti} meta={meta} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-300 hover:text-white">Cancel</Button>
            <Button data-testid={`${testid}-save`} onClick={save} className="bg-[#C9A24B] text-[#0F172A] hover:bg-[#b8923f]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-[#1E293B] border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this record?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/10 text-slate-300 hover:bg-[#0F172A] hover:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testid}-confirm-delete`} onClick={doDelete} className="bg-[#DC2626] text-white hover:bg-[#b91c1c]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
